import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, ListGroup, ListGroupItem, Badge, Button, Input } from "reactstrap";
import apis from "../../../../Api";
import moment from "moment";

function resolveRole(accessLevel) {
  const level = Number(accessLevel);
  if (level === 4) return { key: "student", label: "Aluno" };
  if (level === 3) return { key: "instructor", label: "Instrutor" };
  if (level === 5 || level === 6) return { key: "admin", label: "Administrador" };
  return { key: "other", label: "Usuário" };
}

function decorateUsers(users = []) {
  return users.map(user => {
    const roleInfo = resolveRole(user.accessLevel);
    const isBlocked = Number(user.blocked) === 1;
    return {
      ...user,
      roleKey: roleInfo.key,
      roleLabel: roleInfo.label,
      isBlocked,
      statusLabel: isBlocked ? "Bloqueado" : "Ativo",
      photo: user.photo || user.foto || null
    };
  });
}

export default function RoleModal({
  isOpen,
  onClose,
  title = "Detalhes",
  usersCompany = [],
  classrooms = [],
  companyId = null,
  config = {}, // { roles, blocked, inClassOnly, notInClassOnly }
  onOpenUserDetail,
  onExport // expects openExportModal signature
}) {
  const [loading, setLoading] = useState(false);
  const [roleModalUsers, setRoleModalUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [modalFilter, setModalFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [activeRoles, setActiveRoles] = useState(Array.isArray(config.roles) ? config.roles : []);
  const [activeBlocked, setActiveBlocked] = useState(config.blocked === undefined ? undefined : Boolean(config.blocked));
  const [selectedIds, setSelectedIds] = useState([]);
  const [contextMemberIds, setContextMemberIds] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    // initialize state from config
    setActiveRoles(Array.isArray(config.roles) ? config.roles : []);
    setActiveBlocked(config.blocked === undefined ? undefined : Boolean(config.blocked));
    setModalFilter(config.inClassOnly ? 'in' : (config.notInClassOnly ? 'out' : 'all'));
    setRoleFilter(Array.isArray(config.roles) && config.roles.length === 1 ? config.roles[0] : 'all');
    setCompanyFilter('all');
    setSearch("");
    setSelectedIds([]);
    setContextMemberIds([]);
    setRoleModalUsers([]);

    const inClassOnly = Boolean(config.inClassOnly);
    const notInClassOnly = Boolean(config.notInClassOnly);

    const load = async () => {
      setLoading(true);
      try {
        if ((inClassOnly || notInClassOnly) && companyId) {
          const resp = await apis.getUsersInClassrooms(companyId).catch(() => null);
          const remoteUsers = Array.isArray(resp?.data) ? resp.data : [];
          const decoratedRemote = decorateUsers(remoteUsers);
          const remoteMemberIds = decoratedRemote.map(u => u && u.id !== undefined && u.id !== null ? String(u.id) : null).filter(Boolean);
          setContextMemberIds(remoteMemberIds);
          if (notInClassOnly) {
            const remoteIds = new Set(decoratedRemote.map(u => String(u.id)));
            const candidates = decorateUsers(usersCompany || []).filter(u => !remoteIds.has(String(u.id)));
            setRoleModalUsers(candidates);
            setSelectedIds(candidates.map(u => String(u.id)).filter(Boolean));
          } else {
            setRoleModalUsers(decoratedRemote);
            setSelectedIds(decoratedRemote.map(u => String(u.id)).filter(Boolean));
          }
        } else {
          // default: use decorated company users plus any extra
          setRoleModalUsers(decorateUsers(usersCompany || []));
        }
      } catch (err) {
        console.warn('RoleModal load error', err);
        setRoleModalUsers(decorateUsers(usersCompany || []));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, config, usersCompany, companyId]);

  // compute derived data
  const computed = useMemo(() => {
    const allUnitUsersBase = Array.isArray(usersCompany) ? usersCompany.slice() : [];
    const allUnitUsers = [...allUnitUsersBase];
    roleModalUsers.forEach(user => {
      if (!user || user.id === undefined || user.id === null) return;
      const userId = String(user.id);
      const exists = allUnitUsers.some(existing => String(existing?.id) === userId);
      if (!exists) allUnitUsers.push(user);
    });

    const classUserIds = new Set(Array.isArray(contextMemberIds) ? contextMemberIds.map(String) : []);
    if (classUserIds.size === 0) {
      (Array.isArray(classrooms) ? classrooms : []).forEach(c => {
        if (!c) return;
        const candidateKeys = ['userClass','participants','users','students','classUsers','usersList','class_users','participantsList'];
        candidateKeys.forEach(k => {
          const arr = c[k];
          if (Array.isArray(arr)) {
            arr.forEach(u => {
              if (u === null || u === undefined) return;
              if (typeof u === 'string' || typeof u === 'number') { classUserIds.add(String(u)); return; }
              const uid = u.id ?? u.user ?? u.user_id ?? u.userid ?? u.ID ?? null;
              if (uid !== null && uid !== undefined) classUserIds.add(String(uid));
            });
          }
        });
      });
    }

    const companyUserIdSet = new Set(allUnitUsersBase.filter(u => u && u.id !== undefined && u.id !== null).map(u => String(u.id)));

    const badgeAllCount = allUnitUsers.length;
    const badgeInCount = allUnitUsers.filter(u => u && u.id !== undefined && u.id !== null && classUserIds.has(String(u.id))).length;
    const badgeOutCount = Math.max(badgeAllCount - badgeInCount, 0);

    const roleSearchTerm = (search || "").trim().toLowerCase();

    const filteredRoleModalUsers = allUnitUsers.filter(user => {
      if (!user) return false;
      if (activeRoles.length > 0) {
        const roleKey = (user.roleKey || resolveRole(user.accessLevel)?.key || '').toString();
        if (!activeRoles.includes(roleKey)) return false;
      }
      if (activeBlocked !== undefined) {
        const isBlocked = user.isBlocked ?? (Number(user.blocked) === 1);
        if (Boolean(isBlocked) !== Boolean(activeBlocked)) return false;
      }
      const userId = user.id !== undefined && user.id !== null ? String(user.id) : null;
      if (companyFilter === 'company' && (!userId || !companyUserIdSet.has(userId))) return false;
      if (companyFilter === 'external' && userId && companyUserIdSet.has(userId)) return false;
      if (modalFilter === 'in' && (!userId || !classUserIds.has(userId))) return false;
      if (modalFilter === 'out' && userId && classUserIds.has(userId)) return false;
      if (!roleSearchTerm) return true;
      const fields = [user.name, user.lastName, user.email, user.username, user.roleLabel];
      return fields.filter(Boolean).map(v => v.toString().toLowerCase()).some(v => v.includes(roleSearchTerm));
    });

    const unitMembersInClassCount = filteredRoleModalUsers.filter(user => {
      if (!user || user.id === undefined || user.id === null) return false;
      const userId = String(user.id);
      return companyUserIdSet.has(userId) && classUserIds.has(userId);
    }).length;

    const externalMembersInClassCount = filteredRoleModalUsers.filter(user => {
      if (!user || user.id === undefined || user.id === null) return false;
      const userId = String(user.id);
      return !companyUserIdSet.has(userId) && classUserIds.has(userId);
    }).length;

    return {
      filteredRoleModalUsers,
      classUserIds,
      badgeAllCount,
      badgeInCount,
      badgeOutCount,
      unitMembersInClassCount,
      externalMembersInClassCount
    };
  }, [roleModalUsers, usersCompany, classrooms, search, modalFilter, activeRoles, activeBlocked, companyFilter, contextMemberIds]);

  const { filteredRoleModalUsers, badgeAllCount, badgeInCount, badgeOutCount, unitMembersInClassCount, externalMembersInClassCount, classUserIds } = computed;

  // selection helpers
  const toggleUserSelection = (userId) => {
    if (userId === undefined || userId === null) return;
    const id = String(userId);
    setSelectedIds(prev => {
      const s = new Set(Array.isArray(prev) ? prev.map(String) : []);
      if (s.has(id)) s.delete(id); else s.add(id);
      return Array.from(s);
    });
  };

  const selectVisible = (ids) => {
    const sanitized = Array.isArray(ids) ? ids.filter(i => i !== undefined && i !== null).map(String) : [];
    setSelectedIds(Array.from(new Set(sanitized)));
  };

  const clearSelection = () => setSelectedIds([]);

  const handleExport = () => {
    if (!Array.isArray(filteredRoleModalUsers) || filteredRoleModalUsers.length === 0) return;
    const selectedIdsSet = new Set((Array.isArray(selectedIds) ? selectedIds : []).map(String));

    const rows = filteredRoleModalUsers.map(user => {
      const userId = user?.id;
      const normalizedId = userId !== undefined && userId !== null ? String(userId) : null;
      const membership = normalizedId && classUserIds.has(normalizedId) ? 'in' : 'out';
      return {
        id: user?.id,
        name: user?.name,
        lastName: user?.lastName,
        email: user?.email,
        username: user?.username,
        role: user?.roleLabel,
        status: user?.statusLabel,
        blocked: user?.isBlocked ? "Sim" : "Não",
        blockedReason: user?.blockedReason || "",
        __group: membership
      };
    });

    const selectedCount = rows.filter(row => row.id !== undefined && row.id !== null && selectedIdsSet.has(String(row.id))).length;
    const inCount = rows.filter(row => row.__group === 'in').length;
    const outCount = rows.filter(row => row.__group === 'out').length;

    const groupOptions = [
      { value: 'selected', label: `Selecionados (${selectedCount})`, disabled: selectedCount === 0 },
      { value: 'all', label: `Todos (${rows.length})`, disabled: rows.length === 0 },
      { value: 'in', label: `Usuários da unidade (${inCount})`, disabled: inCount === 0 },
      { value: 'out', label: `Usuários fora (${outCount})`, disabled: outCount === 0 }
    ];

    let defaultGroup = 'all';
    if (selectedCount > 0) defaultGroup = 'selected';
    else if (modalFilter === 'in' && inCount > 0) defaultGroup = 'in';
    else if (modalFilter === 'out' && outCount > 0) defaultGroup = 'out';

    const availableGroup = groupOptions.find(option => option.value === defaultGroup && !option.disabled);
    if (!availableGroup) {
      const fallback = groupOptions.find(option => !option.disabled);
      if (!fallback) return;
      defaultGroup = fallback.value;
    }

    const normalizedSelectedIds = rows.filter(row => row.id !== undefined && row.id !== null && selectedIdsSet.has(String(row.id))).map(row => String(row.id));

    const fields = [
      { key: "id", label: "ID" },
      { key: "name", label: "Nome" },
      { key: "lastName", label: "Sobrenome" },
      { key: "email", label: "E-mail" },
      { key: "username", label: "Usuário" },
      { key: "role", label: "Perfil" },
      { key: "status", label: "Status" },
      { key: "blocked", label: "Bloqueado" },
      { key: "blockedReason", label: "Motivo do bloqueio" }
    ];

    if (onExport) {
      onExport({
        title: title ? `Exportar ${title}` : "Exportar usuários",
        fields,
        data: rows,
        fileName: `usuarios-${moment().format('YYYYMMDD')}.csv`,
        groupOptions,
        defaultGroup,
        selectedIds: normalizedSelectedIds,
        idKey: 'id',
        context: { type: 'roleModalExport' }
      });
    }
  };

  const selectedIdsSet = new Set((selectedIds || []).map(String));
  const selectedCount = selectedIdsSet.size;
  const filteredSelectedCount = filteredRoleModalUsers.filter(user => user && user.id !== undefined && user.id !== null && selectedIdsSet.has(String(user.id))).length;
  const hasRoleData = filteredRoleModalUsers.length > 0;

  const roleBadgeOptions = [
    { value: 'all', label: 'Todos os perfis' },
    { value: 'student', label: 'Alunos' },
    { value: 'instructor', label: 'Instrutores' },
    { value: 'admin', label: 'Administradores' }
  ];
  const companyBadgeOptions = [
    { value: 'all', label: 'Todos os usuários' },
    { value: 'company', label: 'Usuários da unidade' },
    { value: 'external', label: 'Usuários externos' }
  ];

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{title} ({filteredRoleModalUsers.length})</ModalHeader>
      <ModalBody style={{ maxHeight: "60vh", overflowY: "auto" }}>
        {loading ? (
          <div className="py-5 text-center">
            <div className="spinner-border text-primary" role="status"><span className="sr-only">Loading...</span></div>
            <div className="text-muted mt-3">Carregando usuários...</div>
          </div>
        ) : hasRoleData ? (
          <>
            <div className="mb-3">
              <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: '0.75rem' }}>
                <div className="d-flex flex-wrap align-items-center" style={{ gap: '0.5rem' }}>
                  <Badge color={modalFilter === 'all' ? 'primary' : 'light'} pill style={{ cursor: 'pointer' }} onClick={() => setModalFilter('all')}>
                    Todos ({badgeAllCount})
                  </Badge>
                  <Badge color={modalFilter === 'in' ? 'info' : 'light'} pill style={{ cursor: 'pointer' }} onClick={() => setModalFilter('in')}>
                    Usuários da unidade ({badgeInCount})
                  </Badge>
                  <Badge color={modalFilter === 'out' ? 'secondary' : 'light'} pill style={{ cursor: 'pointer' }} onClick={() => setModalFilter('out')}>
                    Usuários fora ({badgeOutCount})
                  </Badge>
                </div>
                <div style={{ minWidth: '40%', maxWidth: '420px' }}>
                  <Input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar por nome, e-mail ou usuário" />
                </div>
              </div>
              <div className="d-flex flex-wrap align-items-center justify-content-between mt-3" style={{ gap: '0.75rem' }}>
                <div className="text-sm text-muted">
                  Selecionados: {selectedCount}
                  {selectedCount > 0 ? ` · ${filteredSelectedCount} na lista atual` : ''}
                </div>
                <div className="d-flex flex-wrap" style={{ gap: '0.5rem' }}>
                  <Button size="sm" color="success" outline onClick={() => selectVisible(filteredRoleModalUsers.map(user => user?.id))} disabled={filteredRoleModalUsers.length === 0}>
                    Selecionar lista
                  </Button>
                  <Button size="sm" color="secondary" outline onClick={clearSelection} disabled={selectedCount === 0}>
                    Limpar seleção
                  </Button>
                </div>
              </div>
              <div className="d-flex flex-wrap align-items-center mt-3" style={{ gap: '0.5rem' }}>
                {roleBadgeOptions.map(option => (
                  <Badge key={option.value} color={option.value === roleFilter ? 'primary' : 'light'} pill style={{ cursor: 'pointer' }} onClick={() => { setRoleFilter(option.value); setActiveRoles(option.value === 'all' ? [] : [option.value]); }}>
                    {option.label}
                  </Badge>
                ))}
              </div>
              <div className="d-flex flex-wrap align-items-center mt-2" style={{ gap: '0.5rem' }}>
                {companyBadgeOptions.map(option => (
                  <Badge key={option.value} color={option.value === companyFilter ? 'info' : 'light'} pill style={{ cursor: 'pointer' }} onClick={() => setCompanyFilter(option.value)}>
                    {option.label}
                  </Badge>
                ))}
              </div>
              <div className="small text-muted mt-3" style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
                <span><strong>Usuários da unidade em turmas:</strong> {unitMembersInClassCount}</span>
                <span><strong>Usuários externos em turmas:</strong> {externalMembersInClassCount}</span>
              </div>
            </div>
            <ListGroup flush>
              {filteredRoleModalUsers.map((user, index) => {
                const hasId = user && user.id !== undefined && user.id !== null;
                const userId = hasId ? String(user.id) : null;
                const checkboxId = hasId ? `role-modal-select-${userId}` : `role-modal-select-${index}`;
                const isSelected = userId ? selectedIdsSet.has(userId) : false;
                return (
                  <ListGroupItem key={hasId ? userId : `user-${index}`} className="d-flex justify-content-between align-items-center py-2" style={isSelected ? { backgroundColor: '#f5f9ff' } : undefined}>
                    <div className="d-flex align-items-center">
                      <div className="custom-control custom-checkbox mr-3">
                        <input type="checkbox" className="custom-control-input" id={checkboxId} checked={isSelected} disabled={!hasId} onChange={() => toggleUserSelection(userId)} />
                        <label className="custom-control-label" htmlFor={checkboxId}></label>
                      </div>
                      <div className="mr-2" style={{ width: "40px", height: "40px", borderRadius: "50%", overflow: "hidden" }}>
                        <img src={(user && user.photo) ? user.photo : "/images/defaultAvatar.png"} alt="avatar" className="rounded-circle" style={{ width: "40px", height: "40px", objectFit: "cover" }} />
                      </div>
                      <div>
                        <div className="font-weight-medium">{user?.name} {user?.lastName}</div>
                        <div className="small text-muted">{user?.email || user?.username || ""}{user?.email || user?.username ? " • " : ""}{hasId ? `ID ${user.id}` : ''}</div>
                        <div className="small text-muted d-flex align-items-center">
                          <span>{user?.roleLabel}</span>
                          <Badge color={user?.isBlocked ? "danger" : "success"} className="ml-2" pill>{user?.statusLabel}</Badge>
                          {user?.blockedReason && user?.isBlocked ? <span className="ml-2 text-danger">({user.blockedReason})</span> : null}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Button size="sm" color="primary" onClick={() => onOpenUserDetail && onOpenUserDetail(user)}>Ver</Button>
                    </div>
                  </ListGroupItem>
                );
              })}
              {filteredRoleModalUsers.length === 0 && (
                <div className="text-center text-muted py-3">Nenhum usuário corresponde à pesquisa.</div>
              )}
            </ListGroup>
          </>
        ) : (
          <div className="text-center text-muted py-3">Nenhum usuário encontrado.</div>
        )}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between align-items-center">
        <div className="text-sm text-muted">
          Selecionados: {selectedCount}
          {selectedCount > 0 ? ` (${filteredSelectedCount} na lista atual)` : ''}
        </div>
        <div>
          <Button color="primary" onClick={handleExport} disabled={!hasRoleData}>Exportar CSV</Button>
          <Button color="secondary" className="ml-2" onClick={onClose}>Fechar</Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
